import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Trash2, Upload, Camera, Eye, Phone, MapPin, CreditCard, ImagePlus, FileImage, CheckCircle2, AlertCircle } from 'lucide-react';

import { getAuthToken } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function GuardRecordsTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '', address: '', id_type: 'Aadhaar', id_number: '' });
  const [idDocFile, setIdDocFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const idDocRef = useRef(null);
  const photoRef = useRef(null);

  const getToken = () => getAuthToken();

  const loadRecords = async () => {
    try {
      const res = await fetch(`${API_URL}/guard-records`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setRecords(await res.json());
    } catch (err) { console.error('Failed to load guard records:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRecords(); }, []);

  const resetForm = () => {
    setForm({ full_name: '', phone: '', address: '', id_type: 'Aadhaar', id_number: '' });
    setIdDocFile(null);
    setPhotoFile(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.id_type || !form.id_number) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!idDocFile) {
      toast.error('ID document upload is mandatory');
      return;
    }
    if (!photoFile) {
      toast.error('Guard photo upload is mandatory');
      return;
    }

    setSaving(true);
    try {
      // Step 1: Create guard record
      const params = new URLSearchParams({
        full_name: form.full_name,
        phone: form.phone,
        address: form.address,
        id_type: form.id_type,
        id_number: form.id_number
      });
      const res = await fetch(`${API_URL}/guard-records?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create');
        return;
      }
      const guard = await res.json();

      // Step 2: Upload ID document
      const idFd = new FormData();
      idFd.append('file', idDocFile);
      const idRes = await fetch(`${API_URL}/guard-records/${guard.id}/upload-id`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: idFd
      });
      if (!idRes.ok) {
        toast.error('Guard created but ID document upload failed. Please upload manually.');
      }

      // Step 3: Upload photo
      const photoFd = new FormData();
      photoFd.append('file', photoFile);
      const photoRes = await fetch(`${API_URL}/guard-records/${guard.id}/upload-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: photoFd
      });
      if (!photoRes.ok) {
        toast.error('Guard created but photo upload failed. Please upload manually.');
      }

      toast.success('Guard record created with documents');
      setShowModal(false);
      resetForm();
      loadRecords();
    } catch (err) {
      console.error('Create guard error:', err);
      toast.error('Failed to create guard record');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (guardId, type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'photo' ? 'image/*' : 'image/*,application/pdf';
    if (type === 'photo') input.capture = 'user';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(`${API_URL}/guard-records/${guardId}/upload-${type === 'photo' ? 'photo' : 'id'}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: fd
        });
        if (res.ok) {
          toast.success(`${type === 'photo' ? 'Photo' : 'ID document'} uploaded`);
          loadRecords();
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Upload failed');
        }
      } catch { toast.error('Upload failed'); }
    };
    input.click();
  };

  const handleViewFile = async (guardId, type) => {
    try {
      const endpoint = type === 'photo' ? 'photo' : 'id-doc';
      const res = await fetch(`${API_URL}/guard-records/${guardId}/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Failed to view file'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete guard record for ${name}?`)) return;
    try {
      const res = await fetch(`${API_URL}/guard-records/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) { toast.success('Deleted'); loadRecords(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <>
      <Card className="shadow-lg border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-gk-navy-light" />
              Security Guard Records ({records.length})
            </CardTitle>
            <Button data-testid="add-guard-record-btn" onClick={() => setShowModal(true)} className="bg-gk-navy-light hover:bg-gk-navy">
              <Plus className="w-4 h-4 mr-1" /> Add Guard
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading...</p>
          ) : records.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No guard records yet. Click "Add Guard" to create one.</p>
          ) : (
            <div className="space-y-3">
              {records.map((g) => (
                <div key={g.id} data-testid={`guard-record-${g.id}`} className="p-4 rounded-lg border bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-gk-navy-light/10 flex items-center justify-center shrink-0 overflow-hidden">
                        {g.photo ? (
                          <button onClick={() => handleViewFile(g.id, 'photo')} className="w-full h-full rounded-full overflow-hidden">
                            <ShieldCheck className="w-6 h-6 text-gk-navy-light" />
                          </button>
                        ) : (
                          <ShieldCheck className="w-6 h-6 text-gk-navy-light" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{g.full_name}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{g.phone}</span>
                          {g.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{g.address}</span>}
                          <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" />{g.id_type}: {g.id_number}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          {g.photo ? (
                            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-600"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Photo</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600"><AlertCircle className="w-2.5 h-2.5 mr-0.5" />No photo</Badge>
                          )}
                          {g.id_document ? (
                            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-600"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />ID doc</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600"><AlertCircle className="w-2.5 h-2.5 mr-0.5" />No ID doc</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleUpload(g.id, 'photo')} className="text-xs h-7">
                        <Camera className="w-3 h-3 mr-1" /> {g.photo ? 'Update' : 'Upload'} Photo
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpload(g.id, 'id')} className="text-xs h-7">
                        <Upload className="w-3 h-3 mr-1" /> {g.id_document ? 'Update' : 'Upload'} ID Doc
                      </Button>
                      {g.photo && (
                        <Button size="sm" variant="outline" onClick={() => handleViewFile(g.id, 'photo')} className="text-xs h-7 text-blue-600">
                          <Eye className="w-3 h-3 mr-1" /> View Photo
                        </Button>
                      )}
                      {g.id_document && (
                        <Button size="sm" variant="outline" onClick={() => handleViewFile(g.id, 'id')} className="text-xs h-7 text-blue-600">
                          <Eye className="w-3 h-3 mr-1" /> View ID
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(g.id, g.full_name)} className="text-xs h-7 text-red-500 hover:text-red-700">
                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Guard Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Security Guard</DialogTitle>
            <DialogDescription>All fields marked with * are mandatory, including document uploads.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label>Full Name (as per Govt ID) *</Label>
              <Input data-testid="guard-name-input" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} required />
            </div>
            <div className="space-y-1.5">
              <Label>Phone Number *</Label>
              <Input data-testid="guard-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required />
            </div>
            <div className="space-y-1.5">
              <Label>Residential Address</Label>
              <Input data-testid="guard-address-input" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <Label>ID Type *</Label>
              <select
                data-testid="guard-id-type-trigger"
                value={form.id_type}
                onChange={(e) => setForm({...form, id_type: e.target.value})}
                className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="Aadhaar">Aadhaar Card</option>
                <option value="PAN">PAN Card</option>
                <option value="Voter ID">Voter ID</option>
                <option value="Driving License">Driving License</option>
                <option value="Passport">Passport</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>ID Number *</Label>
              <Input data-testid="guard-id-number-input" value={form.id_number} onChange={(e) => setForm({...form, id_number: e.target.value})} required />
            </div>

            {/* ID Document Upload */}
            <div className="space-y-1.5">
              <Label>ID Document Upload *</Label>
              <input ref={idDocRef} type="file" accept="image/*,application/pdf" onChange={(e) => setIdDocFile(e.target.files?.[0] || null)} className="hidden" data-testid="guard-id-doc-file-input" />
              <div
                data-testid="guard-id-doc-upload-area"
                onClick={() => idDocRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${idDocFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-gk-teal hover:bg-slate-50'}`}
              >
                {idDocFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileImage className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700 font-medium truncate max-w-[200px]">{idDocFile.name}</span>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-xs text-slate-500">Click to upload ID document (Image/PDF)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Guard Photo Upload */}
            <div className="space-y-1.5">
              <Label>Guard Photo *</Label>
              <input ref={photoRef} type="file" accept="image/*" capture="user" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} className="hidden" data-testid="guard-photo-file-input" />
              <div
                data-testid="guard-photo-upload-area"
                onClick={() => photoRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${photoFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-gk-teal hover:bg-slate-50'}`}
              >
                {photoFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <ImagePlus className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700 font-medium truncate max-w-[200px]">{photoFile.name}</span>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Camera className="w-5 h-5 text-slate-400" />
                    <span className="text-xs text-slate-500">Click to upload or take a photo</span>
                  </div>
                )}
              </div>
            </div>

            <Button data-testid="create-guard-record-btn" type="submit" className="w-full bg-gk-navy-light hover:bg-gk-navy" disabled={saving}>
              {saving ? 'Creating...' : 'Create Guard Record'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
